import globalEventEmitter from '@modules/EventEmitter';
import { EVENTS } from '@modules/EventEmitter/const';
import { PaymentSocket } from '@services/pay';
import { recordPromoterIfDetected } from '@wegic/common';
import { clearAnonymousDeviceIdCookie, getAnonymousUserId } from '@wegic/common/anonymous';
import { getBundleType } from '@wegic/price-ui';
import { Message } from '@wegic/ui';
import '@wegic/ui/dist/components/Message/index.css';
import { handleSubscriptionAndPointsAPI } from '@/components/PayModal/handle';
import { clearAllShortcutSuggestions } from '@/components/QuickPrompts/helper';
import { WEGIC_HOMEPAGE_URL } from '@/const';
import { initPricingMultiYearPageExperiment } from '@/experiments/pricingMultiYearPage';
import { shouldResetAnonymousRuntimeAfterLogin } from '@/helpers/anonymousClaim';
import { clearBuilderAnonymousUser } from '@/helpers/anonymousUser';
import { initOnboardingUIExperiment } from '@/helpers/initClientFeatures';
import { initProductList } from '@/helpers/initProductList';
import { markLogoutInProgress, resetLogoutInProgress } from '@/helpers/logoutState';
import { clearPrepayRetentionShown } from '@/helpers/pay/prepayRetentionStorage';
import { clearAnonymousProjectId } from '@/helpers/project/storage';
import { setCollectUserId } from '@/modules/Collect/helper';
import { navigate } from '@/modules/CustomRouter/handler';
import posthogModule from '@/modules/Posthog';
import { claimAllAnonymousProjects } from '@/services/project';
import { logoutRequest, recordPromoter, requestDeleteUser } from '@/services/user';
import { useAutoInputStore } from '@/stores/autoInput';
import { useChatStore } from '@/stores/chat';
import { usePreviewStore } from '@/stores/preview';
import { useProjectStore } from '@/stores/project';
import { Stage } from '@/stores/project/types';
import { usePublishStore } from '@/stores/publish';
import { useRemixUpgradeModalStore } from '@/stores/remixUpgradeModal';
import { useSettingsModalStore } from '@/stores/settingsModal';
import { useSiteAuditStore } from '@/stores/siteAudit';
import { useTrashStore } from '@/stores/trash';
import { useUserStore } from '@/stores/user';
import { useUserNotifyStore } from '@/stores/userNotify';
import { useVisitorCreditsStore } from '@/stores/visitorCredits';
/**
 * 更新用户积分数据
 */ export function updateUserPointsData(data) {
    const { setUserPoints } = useUserStore.getState();
    if (!(data === null || data === void 0 ? void 0 : data.detailList) || data.detailList.length === 0) {
        setUserPoints(0);
        return;
    }
    const totalPoints = data.detailList.reduce((sum, item)=>{
        return sum + (item.total || 0);
    }, 0);
    setUserPoints(totalPoints);
}
/**
 * 重置所有用户和项目相关的状态
 */ export function resetAllStores() {
    // 重置用户相关状态
    useUserStore.getState().resetUserStore();
    useUserNotifyStore.getState().reset();
    useRemixUpgradeModalStore.getState().resetCreditPurchaseQuota();
    // 重置对话相关状态
    useAutoInputStore.getState().reset();
    useChatStore.getState().resetChat();
    // 重置项目相关状态
    usePreviewStore.getState().reset();
    usePublishStore.getState().reset();
    useProjectStore.getState().reset();
    useSiteAuditStore.getState().reset();
    useTrashStore.getState().reset();
    // 重置设置弹窗状态
    useSettingsModalStore.getState().reset();
    // 清除快捷指令数据
    clearAllShortcutSuggestions();
}
/**
 * 用户信息映射
 */ export function mapAuthUserToUserInfo(data) {
    return {
        userId: data.userId,
        email: data.email,
        userName: data.userName,
        userImage: data.userImage,
        inviteCode: data.inviteCode,
        countryBlock: data.countryBlock,
        freeRightBlock: data.freeRightBlock,
        loginType: data.loginType,
        hasPassword: data.hasPassword,
        createDate: data.createDate
    };
}
/**
 * 写入最新用户信息
 */ export function setLatestUserInfo(userInfo) {
    useUserStore.setState({
        userInfo,
        isLogin: true,
        isLoading: false
    });
}
/**
 * 匿名访问期间创建的项目登录后归属当前用户
 */ async function claimAnonymousProjectsIfNeeded() {
    const anonymousUserId = getAnonymousUserId();
    if (!anonymousUserId) {
        return 'skipped';
    }
    try {
        await claimAllAnonymousProjects(anonymousUserId);
        return 'success';
    } catch (error) {
        console.error('claim anonymous projects failed:', error);
        return 'failed';
    }
}
function getRouteProjectId() {
    const match = window.location.pathname.match(/^\/projects\/([^/?#]+)/);
    return (match === null || match === void 0 ? void 0 : match[1]) ?? null;
}
function resetAnonymousProjectRuntimeStateAfterLogin(claimedAnonymousProjects) {
    const { projectId, stage, setStage, setSessionId } = useProjectStore.getState();
    const shouldReset = shouldResetAnonymousRuntimeAfterLogin({
        claimedAnonymousProjects,
        projectId,
        routeProjectId: getRouteProjectId(),
        stage
    });
    if (!shouldReset) {
        return;
    }
    // 匿名项目进入编辑/生成态后登录，需要回到输入态重新建立登录用户的会话。
    setSessionId('');
    setStage(Stage.Input);
}
/**
 * 处理用户登录成功后的业务初始化
 * @param userInfo 用户信息
 *
 * ⚠️！！！必须先 claim 再 setLatestUserInfo
 */ export async function handleUserLoginEffects(userInfo) {
    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    const claimResult = await claimAnonymousProjectsIfNeeded();
    if (claimResult === 'failed') {
        throw new Error('Failed to claim anonymous projects');
    }
    clearAnonymousProjectId();
    if (claimResult === 'success') {
        clearBuilderAnonymousUser();
    }
    const currentUserInfo = useUserStore.getState().userInfo;
    if ((currentUserInfo === null || currentUserInfo === void 0 ? void 0 : currentUserInfo.userId) !== userInfo.userId) {
        setLatestUserInfo(userInfo);
    }
    await initOnboardingUIExperiment();
    // 初始化支付 Socket
    const userId = userInfo.userId;
    if (userId) {
        PaymentSocket.initialize(userId);
        setCollectUserId(userId);
    }
    resetAnonymousProjectRuntimeStateAfterLogin(claimResult === 'success');
    // 登录成功后清空游客积分
    useVisitorCreditsStore.getState().reset();
    // 触发用户切换事件，通知其他标签页
    globalEventEmitter.emit(EVENTS.USER_CHANGE);
    await handleSubscriptionAndPointsAPI();
    await initPricingMultiYearPageExperiment();
    posthogModule.identifyCurrentUser();
    initProductList();
    void recordPromoterIfDetected(recordPromoter, options.signupAttribution);
}
/**
 * 处理用户退出登录
 */ export async function handleUserLogout() {
    let { redirectUrl = WEGIC_HOMEPAGE_URL, showSuccessMessage = true } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    markLogoutInProgress();
    try {
        await logoutRequest();
    } catch (error) {
        resetLogoutInProgress();
        throw error;
    }
    clearPrepayRetentionShown();
    setCollectUserId(null);
    posthogModule.reset();
    // 断开支付 Socket
    PaymentSocket.disconnect();
    // 清除匿名项目缓存和设备身份，官网会重新创建匿名用户
    clearAnonymousProjectId();
    clearBuilderAnonymousUser();
    clearAnonymousDeviceIdCookie();
    // 重置状态
    resetAllStores();
    // 触发用户切换事件，通知其他标签页
    globalEventEmitter.emit(EVENTS.USER_CHANGE);
    if (showSuccessMessage) {
        Message.success(intl.get('auth.logout.success'));
    }
    window.location.href = redirectUrl;
}
/**
 * 处理用户注销账号
 */ export async function handleDeleteAccount(reasons) {
    await requestDeleteUser({
        reasonList: reasons
    });
    setCollectUserId(null);
    posthogModule.reset();
    // 断开支付 Socket
    PaymentSocket.disconnect();
    // 重置状态
    resetAllStores();
    // 触发用户切换事件，通知其他标签页
    globalEventEmitter.emit(EVENTS.USER_CHANGE);
    navigate('/login');
}
export function getFeedbackHiddenInfo() {
    var _userSubscription_currentProduct_prices, _userSubscription_currentProduct;
    const userInfo = useUserStore.getState().userInfo;
    const userSubscription = useUserStore.getState().userSubscription;
    const currentPlanName = userSubscription === null || userSubscription === void 0 ? void 0 : (_userSubscription_currentProduct = userSubscription.currentProduct) === null || _userSubscription_currentProduct === void 0 ? void 0 : (_userSubscription_currentProduct_prices = _userSubscription_currentProduct.prices) === null || _userSubscription_currentProduct_prices === void 0 ? void 0 : _userSubscription_currentProduct_prices[0].levelName;
    if (!userInfo) {
        return '';
    }
    const params = new URLSearchParams();
    params.append('email', userInfo.email);
    params.append('userId', userInfo.userId);
    params.append('plan', getBundleType(currentPlanName));
    return params.toString();
}
